import { useMemo } from 'react';

import type {
	TotalActionsPerOrganizationItem,
	TotalActionsPerOrganizationTriggerItem,
} from '@/app/types/Dashboard';
import type Highcharts from 'highcharts';

export const config: Highcharts.Options = {
	chart: {
		backgroundColor: 'transparent',
		type: 'column',
		height: 270,
		style: {
			fontFamily: 'Graphik',
		},
	},
	accessibility: {
		enabled: false,
	},
	plotOptions: {
		column: {
			pointPadding: 0,
			groupPadding: 0,
			pointPlacement: 'between',
			dataSorting: {
				enabled: true,
			},
		},
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		itemStyle: {
			fontSize: '10px',
			fontFamily: 'Graphik',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		verticalAlign: 'bottom',
		align: 'left',
		alignColumns: true,
		backgroundColor: 'transparent',

		itemMarginBottom: 0,
		y: 4,
		x: 22,
		maxHeight: 12 * 5,
		itemWidth: 110,

		symbolHeight: 8,
		symbolWidth: 8,
		symbolPadding: 4,
	},
	xAxis: {
		visible: false,
		gridLineWidth: 0,
		labels: {
			enabled: false,
		},
		categories: [''],
	},
	yAxis: {
		title: {
			text: 'Number of Actions',

			style: {
				fontSize: '14px',
				color: '#B6B6B6',
				fontFamily: 'Graphik',
				fontWeight: '600',
				textOverflow: 'none',
				background: 'blue',
				whiteSpace: 'nowrap',
				textAlign: 'left',
			},

			x: -16,
			margin: 0,
		},

		gridLineDashStyle: 'Dash',
		gridLineColor: '#9194A1',
		allowDecimals: false,
		tickAmount: 3,
		endOnTick: true,
		minorGridLineWidth: 0,
	},
	series: [],
};

interface SeriesData {
	name: string;
	data: number[];
	type: string;
	pointWidth: number;
	triggerData: TotalActionsPerOrganizationTriggerItem[];
	itemTotal: number;
}

interface PointFormatterContext {
	series: {
		name: string;
		userOptions: SeriesData;
	};
	color: string;
	y: number;
}

export const useFormattedData = (data: TotalActionsPerOrganizationItem[] = []) => {
	return useMemo(() => {
		try {
			if (!data || data.length === 0) {
				return null;
			}

			const itemSeries: SeriesData[] = data.map((item) => {
				return {
					name: item.organizationName,
					data: [item.organizationTotal],
					type: 'column',
					pointWidth: 20,
					triggerData: item.triggeredData,
					itemTotal: item.organizationTotal,
					color: item.color,
				};
			});

			return {
				...config,
				tooltip: {
					headerFormat: '',
					useHTML: true,
					// eslint-disable-next-line react-hooks/unsupported-syntax
					pointFormatter: function (this: PointFormatterContext) {
						const triggerData = this.series.userOptions.triggerData || [];
						const sortedTriggerData = triggerData.sort(
							(a, b) => parseFloat(b.percentage) - parseFloat(a.percentage)
						);
						const triggerInfo = sortedTriggerData
							.map((trigger: TotalActionsPerOrganizationTriggerItem) => {
								return ` ${trigger.percentage}% ${trigger.trigger || 'N/A'} (${trigger.triggerGroup})`;
							})

							.join('<br>');

						return `
						<div style="height: 111px; overflow-y: scroll; white-space: nowrap; text-overflow: ellipsis; pointer-events: auto !important;">
						<p class="tooltip-header"><span style="color:${this.color}">\u25CF</span>
						${this.series.name}</br><b>
						Total: ${this.y}</p>
						</b>
						<hr>${triggerInfo}
						</div>`;
					},
				},
				series: itemSeries,
			};
		} catch {
			return null;
		}
	}, [data]);
};
